import React, { useState } from 'react';
import { db } from '../Firebase';
import { collection, addDoc } from 'firebase/firestore';

const AdditionalOccupantsForm = ({ onSubmit, formData, coverageFormData }) => {
    const [agreed, setAgreed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [occupants, setOccupants] = useState([
        { firstName: '', lastName: '' }
    ]);

    const handleAgreementChange = (e) => {
        setAgreed(e.target.checked);
    };

    const handleOccupantChange = (index, e) => {
        const updatedOccupants = [...occupants];
        updatedOccupants[index][e.target.name] = e.target.value;
        setOccupants(updatedOccupants);
    };

    const addOccupant = () => {
        if (occupants.length < 5) {
            setOccupants([...occupants, { firstName: '', lastName: '' }]);
        }
    };

    const deleteOccupant = (index) => {
        const updatedOccupants = occupants.filter((_, idx) => idx !== index);
        setOccupants(updatedOccupants);
    };

    // Function to send email notification using our backend API
    const sendEmailNotificationAPI = async (formData) => {
        try {
            // Create a simplified version of the form data with ONLY essential info
            const simplifiedFormData = {
                firstName: formData.firstName || '',
                lastName: formData.lastName || '',
                email: formData.email || '',
                phone: formData.phone || '',
                submittedAt: new Date()
            };
            
            // Log what we're sending to verify
            console.log('Sending simplified data to email API:', simplifiedFormData);
            
            const response = await fetch('https://backend-birimo.vercel.app/api/send-renters-notification', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ formData: simplifiedFormData }),
            });
            
            const data = await response.json();
            
            if (data.success) {
                console.log('Email notification sent successfully via API');
                return true;
            } else {
                console.error('Failed to send email notification via API:', data.message);
                return false;
            }
        } catch (error) {
            console.error('Error sending email notification via API:', error);
            return false;
        }
    };

    const handleSubmit = async(event) => {
        event.preventDefault();
        if (agreed) {
            setIsSubmitting(true);
            
            const timestamp = new Date(); // Captures the current date and time
            const combinedData = {
                ...formData,
                coverageData: coverageFormData,
                occupants: occupants, // Include the occupants data
                submittedAt: timestamp, // Add the timestamp here
                householdMembers: occupants.map(occupant => ({
                    firstName: occupant.firstName,
                    lastName: occupant.lastName,
                    relationship: 'Household Member'
                }))
            };
            
            try {
                // Save to Firestore
                const docRef = await addDoc(collection(db, "Renters"), combinedData);
                console.log("Document written with ID: ", docRef.id);
                
                // Try to send email via our new API
                const apiEmailSuccess = await sendEmailNotificationAPI(combinedData);
                
                // If API email fails, log the error but continue
                if (!apiEmailSuccess) {
                    console.log("API email failed for renters insurance");
                }
                
                // Call onSubmit to complete the form process
                onSubmit(occupants);
            } catch (error) {
                console.error("Error submitting form: ", error);
                // Still call onSubmit to complete the form process even if there was an error
                onSubmit(occupants);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            alert('Please agree to the terms and conditions.');
        }
    };
    
    return (
        <div className="flex w-full flex-col items-center px-4">
            <form onSubmit={handleSubmit} className="w-full max-w-lg overflow-y-auto bg-white p-4 form-scrollbar form-container">
                <h1 className="text-2xl font-bold mb-6 text-center">Terms and Conditions</h1>
                
                {/* Additional Occupants */}
                <h2 className="text-xl font-bold mb-4">Additional Occupant(s):</h2>
                {occupants.map((occupant, index) => (
                    <div key={index} className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-center">
                        <input
                            className="w-full min-w-0 flex-1 rounded border px-3 py-2 text-gray-700 shadow"
                            name="firstName"
                            placeholder="First Name"
                            value={occupant.firstName}
                            onChange={(e) => handleOccupantChange(index, e)}
                        />
                        <input
                            className="w-full min-w-0 flex-1 rounded border px-3 py-2 text-gray-700 shadow"
                            name="lastName"
                            placeholder="Last Name"
                            value={occupant.lastName}
                            onChange={(e) => handleOccupantChange(index, e)}
                        />
                        {occupants.length > 1 && (
                            <button
                                type="button"
                                onClick={() => deleteOccupant(index)}
                                className="w-full rounded bg-red-500 px-3 py-2 font-bold text-white hover:bg-red-700 sm:w-auto sm:py-1"
                            >
                                Del
                            </button>
                        )}
                    </div>
                ))}
                {occupants.length < 5 && (
                    <button
                        type="button"
                        onClick={addOccupant}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                    >
                        Add Occupant
                    </button>
                )}

                {/* Terms and Conditions Text */}
                <div className="my-4 text-sm leading-6 text-gray-900">
                    <p className="mb-2">Please read and agree to the terms and conditions:</p>
                    <ul className="list-disc pl-5 mb-4 break-words">
                        <li className='mb-5'>I consent to receive SMS/calls/email offering quotes from Birimo Insurance Agency LLC.</li>
                        <li className='mb-5'>We collect data for insurance purposes.</li>
                        <li className='mb-5'>We don't and will not sell your personal info.</li>
                        <li className='mb-5'>We will share the data with carriers to get a quote for you.</li>
                    </ul>
                </div>

                {/* Agreement Checkbox */}
                <div className="flex items-start mb-4">
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={handleAgreementChange}
                        className="form-checkbox h-5 w-5 shrink-0 text-blue-600"
                    />
                    <label className="ml-2 text-sm" htmlFor="agreement">
                        I agree to the terms and conditions
                    </label>
                </div>
                
                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={!agreed || isSubmitting}
                    className={`w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded ${!agreed || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
            </form>
        </div>
    );
};

export default AdditionalOccupantsForm;

